import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { Easing, StyleSheet } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
} from 'react-native-reanimated';
import { theme } from '@garnish/constants';
import { TextLink } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { USABLE_NET_BANNER_HEIGHT } from '../constants';
import { useUsableNet } from '../useUsableNet';
import { useUsableNetScrolling } from '../useUsableNetScrolling';

export const UsableNetBanner = () => {
  const isEnabled = useFeatureFlag('cels-3985-usable-net-enabled');
  const { isUsableNetReady } = useUsableNet();
  const { isScrolled } = useUsableNetScrolling();

  // ─── Scroll Animation ──────────────────────────────────────────────────────

  const height = useDerivedValue(() => {
    return withTiming(isScrolled ? 0 : USABLE_NET_BANNER_HEIGHT, ANIMATION);
  });

  const animatedStyle = useAnimatedStyle(() => ({ height: height.value }));

  // ───────────────────────────────────────────────────────────────────────────

  if (!isEnabled || !isUsableNetReady) return null;

  return (
    <Animated.View style={[styles.container, animatedStyle]}>
      <TextLink
        sizeMatch={['12', '16']}
        disabled={isScrolled}
        onPress={enableAccessibility}
      >
        <FormattedMessage {...messages.enableAccessibility} />
      </TextLink>
    </Animated.View>
  );
};

const enableAccessibility = () => {
  (window as unknown as UsableNetWindow)?.enableUsableNetAssistive?.();
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  enableAccessibility: {
    defaultMessage: 'Enable Accessibility',
    description: 'UsableNet | Enable Accessibility',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    alignItems: 'flex-end',
    justifyContent: 'center',
    backgroundColor: theme.colors.CREAM,
    paddingHorizontal: theme.spacing['4'],
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const ANIMATION = { duration: 500, easing: Easing.out(Easing.exp) };

// ─── Types ───────────────────────────────────────────────────────────────────

type UsableNetWindow = {
  enableUsableNetAssistive: () => void;
  disableUsableNetAssistive: () => void;
};
